import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../components/Commons/Input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from "../../contexts/GlobalContext";
import { authHelper } from "../../utils/helpers";
import { checkEmailExists } from "../../utils/helpers/api";
import { Regex, Routes } from "../../utils/constants";
import SolidArrow from "../../components/svg/SolidArrow";
import routes from "../../utils/constants/routes";

interface IRegister {
  isCheck: boolean;
  isExistsEmail: boolean;
  email?: string;
  password?: string;
  passwordConfirm?: string;
}

const RegisterForm: React.FC = () => {
  const { t } = useTranslation(); 
  const navigate = useNavigate();
  const location = useLocation();
  const { editedRegistrationData } = location.state ?? {};
  const [state, setState] = useState<IRegister>({
    isCheck: false,
    isExistsEmail: false,
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const { isCheck, email, password, passwordConfirm, isExistsEmail } = state;
  const { setStateValue } = useGlobalState();
  const [isAuthenticated, setIsAuthenticated] = useState(authHelper.isAuth());
  const checkboxRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const pwdRef = useRef<HTMLInputElement>(null);
  const pwdConfirmRef = useRef<HTMLInputElement>(null);

  

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
    isValid: false,
  });

  // Get saved form data from session storage
  useEffect(() => {
     const urlParams = new URLSearchParams(window.location.search);
     const trackingInflowRoute = urlParams.get("returnto");
     if(trackingInflowRoute){
       localStorage.setItem("trackingInflowRoute",trackingInflowRoute);
     }
    const savedData = sessionStorage.getItem("formData");
    if (savedData) {
      setState(JSON.parse(savedData));
      // Clear local storage
      sessionStorage.removeItem("formData");
    }
  }, []);

  useEffect(() => {
    if (editedRegistrationData) {
      setState((prevState) => ({
        ...prevState,
        email: editedRegistrationData.email,
        isCheck: editedRegistrationData.isCheck,
      }));
    }
  }, [editedRegistrationData]);

  const access_token = authHelper.accessToken();
  if (isAuthenticated && access_token) {
    return (
      <Box className="page__register--result d-flex justify-content-center mt-4">
        <Link to={Routes.USER} className="btn btn-primary center">
          {t("features.register.backToHome")}
        </Link>
      </Box>
    );
  }

  const handlerCheckEmail = async (): Promise<boolean> => {
    setStateValue("loader", true);
    try {
      const lowerCaseEmail = (email ?? "").toLowerCase();
      const result = await checkEmailExists(lowerCaseEmail);
      const { exists } = result.data;
      setState((prevState) => ({
        ...prevState,
        isExistsEmail: exists,
      }));
      return exists;
    } catch (error) {
      console.error("Error checking email:", error);
      return true;
    } finally {
      setStateValue("loader", false);
    }
  };

  const handlerScrollToTop = () => {
    document.documentElement.style.scrollBehavior = "auto";
    setTimeout(() => window.scrollTo(0, 0), 5);
    setTimeout(
      () => (document.documentElement.style.scrollBehavior = "smooth"),
      100
    );
  };

  const validateForm = () => {
    let isValidate = true;
    let errorEmailMess = "";
    let errorPassMess = "";
    let errorPassConfirmMess = "";

    const checkRulePwd = (pwd: string) => {
      const upperCase = /[A-Z]/.test(pwd);
      const lowerCase = /[a-z]/.test(pwd);
      const number = /[0-9]/.test(pwd);
      const specialChar = /[@$!%*?&]/.test(pwd);
      const categories = [upperCase, lowerCase, number, specialChar].filter(
        Boolean
      ).length;

      return categories < 3 ? false : true;
    };

    if (!email) {
      isValidate = false;
      setState((prevState) => ({
        ...prevState,
        isExistsEmail: false,
      }));
      errorEmailMess = "メールアドレス欄は必須です。";
    } else if (email.length > 256) {
      isValidate = false;
      errorEmailMess = "メールアドレス欄は256文字を超えてはいけません。";
    } else if (!Regex.RULE.EMAIL.test(email)) {
      isValidate = false;
      errorEmailMess = "メールアドレスを正しく入力してください";
    } else {
      errorEmailMess = "";
    }

    if (!password) {
      isValidate = false;
      errorPassMess = "パスワード欄は必須です。";
    } else if (password.length < 8 || !checkRulePwd(password)) {
      isValidate = false;
      errorPassMess =
        "大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。";
    } else if (password.length > 50) {
      isValidate = false;
      errorPassMess = "パスワード欄は50文字を超えてはいけません。";
    } else {
      errorPassMess = "";
    }

    if (!passwordConfirm) {
      isValidate = false;
      errorPassConfirmMess = "パスワード:確認欄は必須です。";
    } else if (passwordConfirm.length < 8 || !checkRulePwd(passwordConfirm)) {
      isValidate = false;
      errorPassConfirmMess =
        "大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。";
    } else if (passwordConfirm.length > 50) {
      isValidate = false;
      errorPassConfirmMess = "パスワード:確認欄は50文字を超えてはいけません。";
    } else if (password !== passwordConfirm) {
      errorPassConfirmMess =
        "パスワード:確認欄が新しいパスワードと一致しません。";
      isValidate = false;
    } else {
      errorPassConfirmMess = "";
    }

    setErrors({
      email: errorEmailMess,
      password: errorPassMess,
      passwordConfirm: errorPassConfirmMess,
      isValid: isValidate,
    });
    return isValidate;
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    handlerScrollToTop();
  
    const isFormValid = validateForm();
    
    const lowerCaseEmail = (email ?? "").toLowerCase();
  
    const isEmailExists = await handlerCheckEmail();
    // Save form data to local storage to use in the next step
    sessionStorage.setItem(
      "formData",
      JSON.stringify({ email: lowerCaseEmail, isCheck: isCheck })
    );
    if (errors.email) {
      emailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      emailRef.current?.focus();
    } else if (errors.password) {
      pwdRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      pwdRef.current?.focus();
    } else if (errors.passwordConfirm) {
      pwdConfirmRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      pwdConfirmRef.current?.focus();
    }
    if (isFormValid && !isEmailExists) {
      navigate(Routes.CONFIRM_REGISTER, { state: { email: lowerCaseEmail, password } });
    }
  };

  const handlerChangeValue = (field: string, value: string) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLButtonElement>
  ) => {
    if (e.key === "Enter") {
      if (!isCheck) {
        checkboxRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        checkboxRef.current?.focus();
      } else {
        handleSubmit();
      }
    }
  };

  return (
    <div className="register-page">
      <section>
        <div className="rp-head">
          <div className="step-info">
            <div className="page-title">
              {/* <span>
                <img src="/images/register/Indicator1.png" alt="" />
              </span> */}
              <span>新規会員登録</span>
            </div>
            <p className="description">
              メールアドレス・パスワードを入力し、利用規約をご確認の上「確認」ボタンを押してください。
            </p>
          </div>
        </div>
      </section>
      <form onSubmit={handleSubmit}>
        <section className="register-form">
          <div>
            <div className="form-items">
              <div className="form-item">
                <dt className="fi-label">
                  <label>
                    <span className="text">メールアドレス</span>
                    <span className="item_required">{t("general.required")}</span>
                  </label>
                </dt>
                <dd className="fi-control">
                  <Input
                    validateMess={errors.email}
                    className="mb-2"
                    inputRef={emailRef}
                    error={errors.email}
                    // onKeyDown={handleKeyDown}
                    placeholder="taro@example.com"
                    name="email"
                    id="email"
                    value={state.email}
                    onChange={(value) => {
                      handlerChangeValue("email", value);
                    }}
                    withChecked={true}
                  />
                  {!errors.email && (
                    <span className="txt-14">※ビジネスメールを推奨します</span>
                  )}

                  {isExistsEmail && (
                    <div className="text-danger">
                      <span>
                        このメールアドレスはすでに登録済みです。
                        <a style={{ textDecoration: "none" }} href={Routes.LOGIN}>
                          こちら
                        </a>
                        からログインをお願いします。
                      </span>
                      <br />
                      <span>
                        ※パスワードを忘れた方は
                        <a
                          style={{ textDecoration: "none" }}
                          href={Routes.PASSWORDREMIND}
                        >
                          こちら
                        </a>
                        から再発行をお願いします。
                      </span>
                      <br />
                      <span>
                        ※本人確認メールが届かない場合は
                        <a
                          style={{ textDecoration: "none" }}
                          href={`${Routes.EMAIL_SENT}?email=${encodeURIComponent(
                            email ?? ""
                          )}`}
                        >
                          こちら
                        </a>
                        から再送要求をしてください。
                      </span>
                    </div>
                  )}
                </dd>
              </div>
              <div className="form-item">
                <dt className="fi-label">
                  <label>
                    <span className="text">パスワード</span>
                    <span className="item_required">{t("general.required")}</span>
                  </label>
                </dt>
                <dd className="fi-control">
                  <Input
                    type="password"
                    inputRef={pwdRef}
                    // onKeyDown={handleKeyDown}
                    className="mb-2"
                    error={errors.password}
                    placeholder="入力してください"
                    name="password"
                    id="password"
                    value={state.password}
                    onChange={(value) => {
                      handlerChangeValue("password", value);
                    }}
                    withChecked={true}
                  />
                  {!errors.password && (
                    <span className="txt-14">
                      ※大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。
                    </span>
                  )}
                </dd>
              </div>
              <div className="form-item">
                <dt className="fi-label">
                  <label>
                    <span className="text">
                      {" "}
                      {t("features.register.confirm_password_help_text")}
                    </span>
                  </label>
                </dt>
                <dd className="fi-control">
                  <Input
                    type="password"
                    inputRef={pwdConfirmRef}
                    // onKeyDown={handleKeyDown}
                    error={errors.passwordConfirm}
                    placeholder=""
                    id="passwordConfirm"
                    value={state.passwordConfirm}
                    onChange={(value) => {
                      handlerChangeValue("passwordConfirm", value);
                    }}
                    withChecked={true}
                  />
                </dd>
              </div>
            </div>
          </div>
          {/* <div className="">
            
          
          </div> */}
          <Box
            className="agreement-form-group agreement-fields"
            sx={{
              padding: "20px 15px",
            }}
          >
            <Typography
              className="text-danger"
              sx={{
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 2,
              }}
            >
              {t("features.register.check_terms_msg")}
            </Typography>
            <Box>
              <Link
                to="https://www.sendenkaigi.com/info/rules/"
                style={{ fontSize: "16px", margin: "15px 0 10px" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                利用規約はこちら
              </Link>
            </Box>
            <hr/>
            <Box>
              <Typography fontSize={14} sx={{ marginBottom: 1 }}>
                {t("features.register.personal_information")}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "230px",
                }}
              >
                <div className="agreement-field agreement-business">
                  <dl className="agreement-content">
                    <div>
                      <div className="agreement-information">
                        <div>
                          <h3>1.事業者の名称</h3>
                          <p>株式会社 宣伝会議</p>
                          <h3>
                            2.個人情報保護管理者（若しくはその代理人）の氏名又は職名、所属及び連絡先
                          </h3>
                          <p>
                            管理者名: 執行役員
                            <br /> 所属部署: 株式会社 宣伝会議 営業本部
                            <br /> 連絡先: 電話03-3475-3010
                          </p>
                          <h3>3.個人情報の利用目的</h3>
                          <p>
                            ・会員様向けのサービス提供のため（本人への連絡も含む）
                            <br />
                            ・当社の各種サービスおよびサービスに関連した各種情報のご案内のため
                            <br />
                            ・会員様情報の管理のため
                          </p>
                          <h3>4.個人情報取扱いの委託</h3>
                          <p>
                            当社は事業運営上、前項利用目的の範囲に限って個人情報を外部に委託することがあります。
                          </p>
                          <h3>5.個人情報の開示等の請求</h3>
                          <p>
                            ご本人様は、当社に対してご自身の個人情報の開示等（利用目的の通知、開示、内容の訂正・追加・削除、利用の停止または消去、第三者への提供の停止）に関して、下記の当社問合わせ窓口に申し出ることができます。その際、当社はお客様ご本人を確認させていただいたうえで、合理的な期間内に対応いたします。{" "}
                          </p>
                          <p>
                            〒107-8550　東京都港区南青山3-11-13　新青山東急ビル9F
                            <br />株式会社宣伝会議　個人情報問い合わせ係
                            <br />メールアドレス：compliance@sendenkaigi.co.jp
                            <br />TEL                       ：03-3475-3010（受付け時間9：00〜18：00※）
                            <br />※土・日曜日、祝日、年末年始、ゴールデンウイーク期間は翌営業日以降の対応とさせていただきます。
                          </p>
                          <h3>6.個人情報を提供されることの任意性について</h3>
                          <p>
                            {" "}
                            ご本人様が当社に個人情報を提供されるかどうかは任意によるものです。ただし、必要な項目をいただけない場合、適切な対応ができない場合があります。
                          </p>
                        </div>
                      </div>
                    </div>
                  </dl>
                </div>
              </Box>
            </Box>
          </Box>
          <div>
            <div className="form-items">
              <div className="form-item agree_message">
                <div>
                  <dt className="fi-label">
                    <label>
                      <span className="text">利用規約の確認</span>
                      <span className="item_required">
                        {t("general.required")}
                      </span>
                    </label>
                  </dt>
                  <dd className="fi-control">
                    <FormControlLabel
                      ref={checkboxRef}
                      control={
                        <Checkbox
                          inputRef={checkboxRef}
                          sx={{
                            padding: 0,
                            "& .MuiSvgIcon-root": { fontSize: 17 },
                            "&.Mui-checked": {
                              color: "#009999",
                            },
                          }}
                          checked={isCheck}
                          onChange={() => {
                            setState((prevState) => ({
                              ...prevState,
                              isCheck: !isCheck,
                            }));
                          }}
                          // onKeyDown={}
                          // onKeyDown={handleKeyDown}
                        />
                      }
                      label={t("features.register.agree_message")}
                      sx={{
                        color: "#313534",
                        marginTop: "10px",
                        gap: "0.5rem",
                        marginBottom: {
                          xs: "13px",
                          md: "18px",
                        },
                        mx: "auto",
                        alignSelf: "center",
                        "& .MuiFormControlLabel-label": {
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                          fontWeight: 500,
                        },
                      }}
                    />
                  </dd>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="buttons">
              <button
                type="submit"
                // onClick={handleSubmit}
                disabled={!isCheck}
                className="btn-submit"
              >
                <span>確認</span>
              </button>
            </div>
            <div className="to-login">
              <Link to={routes.LOGIN}>
                <span>すでに会員の方</span>
                <span>
                  <SolidArrow />
                </span>
              </Link>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
};

export default RegisterForm;
